/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { theme } from '../styles/theme';

/* ============================================
   USER BUTTON
   ============================================ */
export const UserButtonContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const UserButtonButton = styled.button<{ $detailed?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  border-radius: ${(props) => (props.$detailed ? theme.radius.sm : theme.radius.full)};
  background-color: ${(props) => (props.$detailed ? theme.colors.bgWhite : 'transparent')};
  border: ${(props) => (props.$detailed ? `1px solid ${theme.colors.border}` : 'none')};
  cursor: pointer;
  transition: all ${theme.transition.base};

  &:hover {
    ${(props) =>
      props.$detailed
        ? `
      background-color: ${theme.colors.bgLight};
      opacity: 1;
    `
        : `
      opacity: 0.8;
    `}
  }
`;

export const UserButtonAvatar = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: ${theme.radius.full};
  background-color: ${theme.colors.primary};
  color: ${theme.colors.bgWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${theme.fontSize.sm};
  font-family: ${theme.fontFamily.base};
  overflow: hidden;
`;

export const UserButtonAvatarImage = styled.img`
  border-radius: ${theme.radius.full};
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

export const UserButtonAvatarInitials = styled.span`
  color: ${theme.colors.bgWhite};
  font-weight: 600;
  font-size: ${theme.fontSize.sm};
`;

export const UserButtonInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
`;

export const UserButtonName = styled.div`
  font-size: ${theme.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.25rem;
  text-align: left;
  font-family: ${theme.fontFamily.base};
`;

export const UserButtonEmail = styled.div`
  font-size: ${theme.fontSize.xs};
  color: ${theme.colors.textSecondary};
  line-height: 1rem;
  text-align: left;
  font-family: ${theme.fontFamily.base};
`;

export const UserButtonMenu = styled.div<{
  $openUpward?: boolean;
  $horizontalOffset?: number;
}>`
  position: absolute;
  ${(props) =>
    props.$openUpward
      ? `
    bottom: 100%;
    margin-bottom: ${theme.spacing[2]};
  `
      : `
    top: 100%;
    margin-top: ${theme.spacing[2]};
  `}
  left: 0;
  ${(props) =>
    props.$horizontalOffset
      ? `
    transform: translateX(${props.$horizontalOffset}px);
  `
      : ''}
  background-color: ${theme.colors.bgWhite};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  box-shadow: ${theme.shadow.lg};
  padding: ${theme.spacing[2]};
  min-width: 200px;
  z-index: 50;
`;

export const UserButtonMenuItem = styled.button<{ $signout?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.radius.sm};
  cursor: pointer;
  transition: background-color ${theme.transition.fast};
  font-size: ${theme.fontSize.sm};
  color: ${(props) => (props.$signout ? theme.colors.error : theme.colors.text)};
  font-family: ${theme.fontFamily.base};
  background: none;
  border: none;
  width: 100%;
  text-align: left;

  &:hover {
    background-color: ${theme.colors.bgLight};
  }
`;

export const UserButtonMenuItemIcon = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

/* ============================================
   USER PROFILE MODAL
   ============================================ */
export const ProfileModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${theme.spacing[4]};
`;

export const ProfileModalContainer = styled.div`
  background-color: ${theme.colors.bgWhite};
  border-radius: ${theme.radius.xl};
  box-shadow: ${theme.shadow.lg};
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: ${theme.fontFamily.base};
`;

export const ProfileModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border};
`;

export const ProfileModalTitle = styled.h2`
  font-size: ${theme.fontSize.lg};
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text};
  margin: 0;
`;

export const ProfileModalCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: ${theme.radius.md};
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  transition: all ${theme.transition.fast};

  &:hover {
    background-color: ${theme.colors.bgLight};
    color: ${theme.colors.text};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const ProfileModalBody = styled.div`
  padding: ${theme.spacing[6]};
  overflow-y: auto;
  flex: 1;
`;

export const ProfileAvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${theme.spacing[6]};
`;

export const ProfileAvatar = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${theme.radius.full};
  background-color: ${theme.colors.primary};
  color: ${theme.colors.bgWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${theme.fontWeight.semibold};
  font-size: ${theme.fontSize['2xl']};
  overflow: hidden;
`;

export const ProfileAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ProfileFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

export const ProfileField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

export const ProfileFieldLabel = styled.label`
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  color: ${theme.colors.textSecondary};
  text-transform: capitalize;
`;

export const ProfileFieldValue = styled.div`
  font-size: ${theme.fontSize.base};
  color: ${theme.colors.text};
  padding: ${theme.spacing[2]} 0;
  word-break: break-word;
`;

export const ProfileFieldInput = styled.input`
  width: 100%;
  height: ${theme.sizes.input.height};
  padding: 0 ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  font-size: ${theme.fontSize.base};
  font-family: ${theme.fontFamily.base};
  color: ${theme.colors.text};
  background-color: ${theme.colors.bgWhite};
  transition: border-color ${theme.transition.fast};

  &:focus {
    outline: none;
    border-color: ${theme.colors.borderFocus};
  }

  &:disabled {
    background-color: ${theme.colors.bgLight};
    color: ${theme.colors.textSecondary};
    cursor: not-allowed;
  }
`;

export const ProfileModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border};
`;

export const ProfileButton = styled.button<{ $primary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  height: ${theme.sizes.button.height};
  padding: 0 ${theme.spacing[4]};
  border-radius: ${theme.radius.md};
  font-size: ${theme.fontSize.sm};
  font-weight: ${theme.fontWeight.medium};
  font-family: ${theme.fontFamily.base};
  cursor: pointer;
  transition: all ${theme.transition.fast};

  ${(props) =>
    props.$primary
      ? `
    background-color: ${theme.colors.primary};
    color: ${theme.colors.bgWhite};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${theme.colors.primaryHover};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `
      : `
    background-color: ${theme.colors.bgWhite};
    color: ${theme.colors.text};
    border: 1px solid ${theme.colors.border};

    &:hover:not(:disabled) {
      background-color: ${theme.colors.bgLight};
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
`;

export const ProfileSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid transparent;
  border-top-color: currentColor;
  border-radius: ${theme.radius.full};
  animation: spin 0.6s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
